import { fetchUtils } from 'react-admin';
import {
  CancelSubscriptionByIdParams,
  CancelSubscriptionByIdResult,
  CreateSubscriptionParams,
  CreateSubscriptionResult,
  GetServiceByIdParams,
  GetServiceByIdResult,
  GetServiceRatesResult,
  GetServiceTypesParams,
  GetServiceTypesResult,
  GetServicesParams,
  GetServicesResult,
  GetSubscriptionCancelReasonsResult,
  GetSubscriptionDataByIdParams,
  GetSubscriptionDataByIdResult,
  GetSubscriptionServiceByIdResult,
  GetSubscriptionServiceRatesCompareParams,
  GetSubscriptionServiceRatesCompareResult,
  GetUserSubscriptionTransactionsByIdParams,
  GetUserSubscriptionTransactionsByIdResult,
  GetUserSubscriptionsByIdParams,
  GetUserSubscriptionsByIdResult,
  RefundSubscriptionTransactionParams,
  RefundSubscriptionTransactionResult,
  FullSubSvcItem,
  ServiceRateItem,
  ServiceTypeItem,
  SetSubscriptionChangeRateParams,
  SetSubscriptionChangeRateResult,
  SetSubscriptionDataByIdParams,
  SetSubscriptionDataByIdResult,
  TaxQuoteRequest,
  TaxQuoteResponse,
  UpdateSubscriptionPaymentMethodResult,
  GetServiceRatesParams,
  GetServiceRateByIdParams,
  GetServiceRateByIdResult,
  GetServiceTypeFieldsParams,
  GetServiceTypeFieldsResult,
  CreateServiceRateParams,
  CreateServiceRateResult,
  CreateSubscriptionServiceParams,
  CreateSubscriptionServiceResult,
  GetGiftSubscriptionSearchParams,
  GetGiftSubscriptionSearchResult,
  GetGiftSubscriptionDetailParams,
  GetGiftSubscriptionDetailResult,
  GetSubscriptionRateNamesParams,
  GetSubscriptionRateNamesResult,
} from 'utility/types/dataProvider';
import { stringifyOpts } from 'providers/clients/httpClient';
import qs from 'qs';
import { httpClient, qsOptions } from 'providers/clients/httpClient';

// -----------------------------------------------
// ------------ SUBSCRIPTIONS PROVIDER -----------
// -----------------------------------------------
export const subscriptionsProvider = {
  // Search site subscription services
  // Get site subscription services
  getServices: async (params: GetServicesParams): Promise<GetServicesResult> => {
    try {
      const payload = { ...params };
      const path = `/subscription/services/?${fetchUtils.queryParameters(
        payload,
        stringifyOpts,
      )}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetServicesResult = {
        status: data?.status || status,
        data: {
          total: data?.data?.total,
          next: data?.data?.next,
          prev: data?.data?.prev,
          items: data?.data?.items?.map((item: FullSubSvcItem) => ({ ...item })),
        },
      };
      return result;
    } catch (error) {
      console.error('[getServices] Error:', error);
      throw error;
    }
  },

  getServiceById: async (params: GetServiceByIdParams): Promise<GetServiceByIdResult> => {
    try {
      const path = `/subscription/services/${params.id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetServiceByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[getServiceById] Error:', error);
      throw error;
    }
  },

  // Get site subscription service types
  getServiceTypes: async (
    params: GetServiceTypesParams,
  ): Promise<GetServiceTypesResult> => {
    try {
      const payload = {
        ...(params.activated && { activated: params.activated }),
        ...(params.available && { available: params.available }),
        ...(params.query && { query: params.query }),
        ...(params.limit && { limit: params.limit > 100 ? 99 : params.limit }),
        ...(params.page && { page: params.page }),
      };
      const path = `/subscription/services/types/?${fetchUtils.queryParameters(
        payload,
        stringifyOpts,
      )}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetServiceTypesResult = {
        status: data?.status || status,
        data: {
          total: data?.data?.total,
          next: data?.data?.next,
          prev: data?.data?.prev,
          items: data?.data?.items?.map((item: ServiceTypeItem) => ({ ...item })),
        },
      };
      return result;
    } catch (error) {
      console.error('[getServiceTypes] Error:', error);
      throw error;
    }
  },

  // Get search site subscription service rates
  getServiceRates: async (
    serviceId?: number,
    params?: GetServiceRatesParams,
  ): Promise<GetServiceRatesResult> => {
    try {
      if (!serviceId) {
        throw new Error('[getServiceRates] Missing required parameters!');
      }

      // Prepare query parameters
      const payload = {
        ...(params?.is_active !== undefined && { is_active: params.is_active }),
        ...(params?.page !== undefined && { page: params.page }),
        ...(params?.limit !== undefined && { limit: params.limit }),
        ...(params?.allow_hidden !== undefined && { allow_hidden: params.allow_hidden }),
      };

      const path = `/subscription/services/${serviceId}/rates/?${fetchUtils.queryParameters(
        payload,
        stringifyOpts,
      )}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetServiceRatesResult = {
        status: data?.status || status,
        data: {
          total: data?.data?.total,
          next: data?.data?.next,
          prev: data?.data?.prev,
          facets: data?.data?.facets,
          items: data?.data?.items?.map((item: ServiceRateItem) => ({ ...item })),
        },
      };

      return result;
    } catch (error) {
      console.error('[getServiceRates] Error:', error);
      throw error;
    }
  },

  // get rate by id /subscription/services/{id}/rates/{rate_id}/
  getServiceRateById: async (
    params: GetServiceRateByIdParams,
  ): Promise<GetServiceRateByIdResult> => {
    try {
      const pathParams = params?.path;
      const id = pathParams?.id; // sub service id
      const rate_id = pathParams?.rate_id; // rate id

      if (!id || !rate_id) {
        throw new Error('[getServiceRateById] Missing required paramaters.');
      }

      const url = `/subscription/services/${id}/rates/${rate_id}/`;

      const { data, status } = await httpClient({
        url: url,
        method: 'GET',
      });

      const result: GetServiceRateByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getServiceRateById] Error:', error);
      throw error;
    }
  },

  // Create a new subscription service
  createSubscriptionService: async (
    body: CreateSubscriptionServiceParams,
  ): Promise<CreateSubscriptionServiceResult> => {
    try {
      if (!body?.type) {
        throw new Error('[createSubscriptionService] Missing required type parameter!');
      }

      const path = `/subscription/services/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(body),
      });

      const result: CreateSubscriptionServiceResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[createSubscriptionService] Error:', error);
      throw error;
    }
  },

  updateSubscriptionService: async (
    id: string,
    body: Partial<FullSubSvcItem>,
  ): Promise<GetServiceByIdResult> => {
    try {
      if (!id) {
        throw new Error('[updateSubscriptionService] Missing required parameters!');
      }

      const path = `/subscription/services/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(body),
      });

      const result: GetServiceByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[updateSubscriptionService] Error:', error);
      throw error;
    }
  },

  // Create service rate /subscription/services/{id}/rates/
  createServiceRate: async (
    id: number,
    rateData: CreateServiceRateParams,
  ): Promise<CreateServiceRateResult> => {
    try {
      if (!id) {
        throw new Error('[createServiceRate] Missing required parameters!');
      }

      const path = `/subscription/services/${id}/rates/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(rateData),
      });

      const result: CreateServiceRateResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[createServiceRate] Error:', error);
      throw error;
    }
  },

  // Update service rate /subscription/services/{id}/rates/{rate_id}/
  updateServiceRate: async (
    serviceId: number,
    rateId: string,
    rateData: CreateServiceRateParams,
  ): Promise<CreateServiceRateResult> => {
    try {
      if (!serviceId || !rateId) {
        throw new Error('[updateServiceRate] Missing required parameters!');
      }

      const path = `/subscription/services/${serviceId}/rates/${rateId}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(rateData),
      });

      const result: CreateServiceRateResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[updateServiceRate] Error:', error);
      throw error;
    }
  },

  // Get subscription tax quote
  getTaxQuote: async (
    serviceId: string,
    quoteData: TaxQuoteRequest,
  ): Promise<TaxQuoteResponse> => {
    try {
      const path = `/subscription/services/${serviceId}/quote/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(quoteData),
      });

      const result: TaxQuoteResponse = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[getTaxQuote] Error:', error);
      throw error;
    }
  },

  // Create subscription
  createSubscription: async (
    params: CreateSubscriptionParams,
  ): Promise<CreateSubscriptionResult> => {
    try {
      const path = `/subscription/subscriptions/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(params),
      });

      const result: CreateSubscriptionResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[createSubscription] Error', error);
      throw error;
    }
  },

  // Get /subscription/members/{id}/subscriptions
  getUserSubscriptionsById: async ({
    query,
    path,
  }: GetUserSubscriptionsByIdParams): Promise<GetUserSubscriptionsByIdResult> => {
    try {
      const pathId = path?.id;

      if (!pathId) {
        throw new Error('Missing required parameters!');
      }

      const page = query?.page;
      const limit = query?.limit || 99;
      const sort = query?.sort;

      const payload = {
        ...(page && { page: query?.page }),
        ...(limit && { limit: query?.limit || 99 }),
        ...(sort && { sort: query?.sort }),
      };

      const baseUrl = `/subscription/members/${pathId}/subscriptions/`;

      const hasPayload = Object.keys(payload).length > 0;
      // the qs library can auto flatten objects to bracket
      // eg: { previews: { size1:'550w' } } -> 'previews[size1]': '550w'
      const qpStr = hasPayload ? qs.stringify(payload, qsOptions) : '';

      // Build query string and final path
      const finalUrl = qpStr ? `${baseUrl}?${qpStr}` : baseUrl;

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'GET',
      });

      const result: GetUserSubscriptionsByIdResult = {
        status: data?.status || status,
        data: { ...data?.data },
      };
      return result;
    } catch (error) {
      console.error('[getUserSubscriptionsById] Error:', error);
      throw error;
    }
  },

  // get /subscription/subscriptions/{id}/
  getSubscriptionDataById: async ({
    id,
  }: GetSubscriptionDataByIdParams): Promise<GetSubscriptionDataByIdResult> => {
    try {
      const path = `/subscription/subscriptions/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetSubscriptionDataByIdResult = {
        status: data?.status || status,
        data: { ...data?.data },
      };

      return result;
    } catch (error) {
      console.error('[getSubscriptionDataById] Error', error);
      throw error;
    }
  },

  // Update subscription by ID
  // POST /subscription/subscriptions/{id}/
  updateSubscriptionById: async (
    id: string,
    body: Record<string, any>,
  ): Promise<SetSubscriptionDataByIdResult> => {
    try {
      if (!id || !body || Object.keys(body).length === 0) {
        throw new Error('[updateSubscriptionById] Missing id or body');
      }
      const path = `/subscription/subscriptions/${id}/`;
      const { status, data } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(body),
      });

      const result: SetSubscriptionDataByIdResult = {
        status: status,
        ...(data?.data && { data: data.data }),
      };

      return result;
    } catch (error) {
      console.error('[updateSubscriptionById] Error:', error);
      throw error;
    }
  },

  // post subscription/subscriptions/{id} (expire time update & delivery address)
  setSubscriptionDataById: async ({
    id,
    expire_time,
    delivery_address,
  }: SetSubscriptionDataByIdParams): Promise<SetSubscriptionDataByIdResult> => {
    try {
      if (!id) {
        throw new Error('Missing required id parameter!');
      }

      if (!expire_time && !delivery_address) {
        throw new Error('Must provide either expire_time or delivery_address to update!');
      }

      const path = `/subscription/subscriptions/${id}/`;
      const body: any = {};

      // Add expire_time to body if provided
      if (expire_time) {
        body.expire_time = expire_time;
      }

      // Add delivery_address to body if provided
      if (delivery_address) {
        body.delivery_address = delivery_address;
      }

      const { status } = await httpClient({
        url: path,
        method: 'POST',
        data: body,
      });

      // success is a 204 no content status
      const result: SetSubscriptionDataByIdResult = {
        status: status,
      };

      return result;
    } catch (error) {
      console.error('[setSubscriptionDataById] Error', error);
      throw error;
    }
  },

  // get /subscription/subscriptions/{id}/transactions/
  getUserSubscriptionTransactionsById: async ({
    id,
    page,
    limit,
  }: GetUserSubscriptionTransactionsByIdParams): Promise<GetUserSubscriptionTransactionsByIdResult> => {
    try {
      const payload = {
        ...(page && { page: page }),
        ...(limit && { limit: limit > 100 ? 99 : limit }),
      };

      const path = `/subscription/subscriptions/${id}/transactions/?${fetchUtils.queryParameters(
        payload,
        stringifyOpts,
      )}`;

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetUserSubscriptionTransactionsByIdResult = {
        status: data?.status,
        data: { ...data?.data },
      };

      return result;
    } catch (error) {
      console.error('[getUserSubscriptionTransactionsById] Error', error);
      throw error;
    }
  },

  // post - /subscription/subscriptions/{id}/cancel/?reason_code={reason_code}&reason={reason}
  cancelSubscriptionById: async ({
    id,
    reason_code,
    reason,
  }: CancelSubscriptionByIdParams): Promise<CancelSubscriptionByIdResult> => {
    try {
      if (!id || !reason_code) {
        throw new Error('Missing required paramaters!');
      }

      const basePath = `/subscription/subscriptions/${id}/cancel/`;
      const params = new URLSearchParams({
        ...(reason_code && { reason_code }),
        ...(reason && { reason: reason.trim() }),
      });

      const stringPath = `${basePath}?${params.toString()}`;

      const { data, status } = await httpClient({
        url: stringPath,
        method: 'POST',
      });

      // success is a 204 no content status
      const result: CancelSubscriptionByIdResult = {
        status: status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[cancelSubscriptionById] Error', error);
      throw error;
    }
  },

  // get - /subscription/introspection/cancel_reasons/
  getSubscriptionCancelReasons: async (): Promise<GetSubscriptionCancelReasonsResult> => {
    try {
      const path = `/subscription/introspection/cancel_reasons/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetSubscriptionCancelReasonsResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getSubscriptionCancelReasons] Error', error);
      throw error;
    }
  },

  // refund success is 204 - /subscription/subscriptions/{id}/transactions/{transaction_id}/refund/
  refundSubscriptionTransaction: async ({
    id,
    transactionId,
  }: RefundSubscriptionTransactionParams): Promise<RefundSubscriptionTransactionResult> => {
    try {
      if (!id || !transactionId) {
        throw new Error('Missing required parameters!');
      }

      const path = `/subscription/subscriptions/${id}/transactions/${transactionId}/refund/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
      });

      // Return the status and data. Should be Status 204 if successful.
      const result: RefundSubscriptionTransactionResult = {
        status: status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[refundSubscriptionTransaction] Error', error);
      throw error;
    }
  },

  // get /subscription/services/{id} - retrieve service data by id
  getSubscriptionServiceById: async (
    id: string,
  ): Promise<GetSubscriptionServiceByIdResult> => {
    try {
      if (!id) {
        throw new Error('Missing required parameters!');
      }
      const path = `/subscription/services/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetSubscriptionServiceByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[getSubscriptionServiceById] Error', error);
      throw error;
    }
  },

  // get subscription/services/{id}/rates/compare/
  getCompareSubscriptionServiceRates: async (
    params: GetSubscriptionServiceRatesCompareParams,
  ): Promise<GetSubscriptionServiceRatesCompareResult> => {
    const { id, rate_id, save_offers, allow_hidden, page, limit } = params;

    if (!id || !rate_id) {
      throw new Error('Missing required parameters!');
    }

    try {
      const basePath = `/subscription/services/${id}/rates/compare/`;

      const params = new URLSearchParams({
        rate_id: rate_id,
        ...(save_offers !== undefined && { save_offers: String(save_offers) }),
        ...(allow_hidden !== undefined && { allow_hidden: String(allow_hidden) }),
        ...(page && { page: String(page) }),
        ...(limit && { limit: String(limit) }),
      });

      const stringPath = `${basePath}?${params.toString()}`;

      const { data, status } = await httpClient({
        url: stringPath,
        method: 'GET',
      });

      const result: GetSubscriptionServiceRatesCompareResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getCompareSubscriptionServiceRates] Error', error);
      throw error;
    }
  },

  // subscription/subscriptions/{id}/change_rate/ - 204 success
  setSubscriptionChangeRate: async (
    params: SetSubscriptionChangeRateParams,
  ): Promise<SetSubscriptionChangeRateResult> => {
    try {
      const { id, rate_id } = params;

      if (!id || !rate_id) {
        throw new Error('Missing required parameters!');
      }

      const path = `/subscription/subscriptions/${id}/change_rate/?rate_id=${rate_id}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
      });

      const result: SetSubscriptionChangeRateResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[setSubscriptionChangeRate] Error', error);
      throw error;
    }
  },

  // subscription/subscriptions/{id}/update_payment_method/ - 204 success
  updateSubscriptionPaymentMethod: async (
    id: string,
    paymentMethodId: string,
  ): Promise<UpdateSubscriptionPaymentMethodResult> => {
    try {
      if (!id || !paymentMethodId) {
        throw new Error('Missing required parameters!');
      }

      const url = `/subscription/subscriptions/${id}/update_payment_method/?payment_method=${paymentMethodId}`;

      const { data, status } = await httpClient({
        url: url,
        method: 'POST',
      });

      const result: UpdateSubscriptionPaymentMethodResult = {
        status: status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[updateSubscriptionPaymentMethod] error:', error);
      throw error;
    }
  },

  // Get field configurations for a specific service type
  getServiceTypeFields: async (
    params: GetServiceTypeFieldsParams,
  ): Promise<GetServiceTypeFieldsResult> => {
    try {
      if (!params.typeId) {
        throw new Error('[getServiceTypeFields] Missing required typeId parameter');
      }

      const path = `/subscription/services/types/${params.typeId}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      let fields = [];

      if (data?.data) {
        fields = data.data.config || [];
      }

      // Process fields to normalize types
      const processedFields = fields.map((field: any) => {
        // Handle integer type fields (like renewal_notice_days)
        if (
          field.type === 'integer' ||
          (field.type === 'number' && field.config?.format === 'integer')
        ) {
          // Normalize to number type for DynamicFormField
          return {
            ...field,
            type: 'number',
          };
        }

        return field;
      });

      const result: GetServiceTypeFieldsResult = {
        status: data?.status || status,
        data: {
          fields: processedFields,
          notice_capabilities: data?.data?.notice_capabilities || null,
        },
      };
      return result;
    } catch (error) {
      console.error('[getServiceTypeFields] Error:', error);
      throw error;
    }
  },

  // -----------------------------------------------
  // Gift Subscriptions - CMSADMIN-1560
  // -----------------------------------------------

  // GET /subscription/gifts/search/
  getGiftSubscriptionSearch: async (
    params: GetGiftSubscriptionSearchParams,
  ): Promise<GetGiftSubscriptionSearchResult> => {
    try {
      const payload = {
        ...(params.code && { code: params.code }),
        ...(params.purchaser_email && { purchaser_email: params.purchaser_email }),
        ...(params.recipient_email && { recipient_email: params.recipient_email }),
        ...(params.delivered != null && { delivered: params.delivered }),
        ...(params.redeemed != null && { redeemed: params.redeemed }),
        ...(params.export != null && { export: params.export }),
        ...(params.limit && { limit: params.limit }),
        ...(params.page && { page: params.page }),
        ...(params.sort && { sort: params.sort }),
        ...(params.dir && { dir: params.dir }),
      };

      const baseUrl = '/subscription/gifts/search/';
      const hasPayload = Object.keys(payload).length > 0;
      const qpStr = hasPayload ? qs.stringify(payload, qsOptions) : '';
      const finalUrl = qpStr ? `${baseUrl}?${qpStr}` : baseUrl;

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'GET',
      });

      const result: GetGiftSubscriptionSearchResult = {
        status: data?.status || status,
        data: { ...data?.data },
      };
      return result;
    } catch (error) {
      console.error('[getGiftSubscriptionSearch] Error:', error);
      throw error;
    }
  },

  // GET /subscription/gifts/{code}/
  getGiftSubscriptionDetail: async (
    params: GetGiftSubscriptionDetailParams,
  ): Promise<GetGiftSubscriptionDetailResult> => {
    try {
      if (!params.code) {
        throw new Error('Missing required gift code parameter');
      }

      const path = `/subscription/gifts/${params.code}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetGiftSubscriptionDetailResult = {
        status: data?.status || status,
        data: { ...data?.data },
      };
      return result;
    } catch (error) {
      console.error('[getGiftSubscriptionDetail] Error:', error);
      throw error;
    }
  },

  // GET /subscription/rates/search_names/
  getSubscriptionRateNames: async (
    params?: GetSubscriptionRateNamesParams,
  ): Promise<GetSubscriptionRateNamesResult> => {
    try {
      // params is optional, fallback to empty
      const args = params || {};

      const payload = {
        ...(args?.query && { query: args.query }),
      };

      const baseUrl = '/subscription/rates/search_names/';
      const hasPayload = Object.keys(payload).length > 0;
      const qpStr = hasPayload ? qs.stringify(payload, qsOptions) : '';
      const finalUrl = qpStr ? `${baseUrl}?${qpStr}` : baseUrl;

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'GET',
      });

      const result: GetSubscriptionRateNamesResult = {
        status: data?.status || status,
        data: data?.data || [],
      };
      return result;
    } catch (error) {
      console.error('[getSubscriptionRateNames]:', error);
      throw error;
    }
  },
};
